/**
 * New Case Wizard — 4-step flow:
 *  Step 1: Source reference (claim, loan, contract lookup)
 *  Step 2: Case details (type, role, priority, court, exposure, assignee)
 *  Step 3: Parties (opposing party + counsels + key witnesses)
 *  Step 4: Schedule first event (mention, hearing, deadline)
 *  Summary: review and submit
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CaseManagementService } from '../case-management.service';
import { AlfrescoRepoBrowseService, RepoNode } from '../../wf-runtime/services/alfresco-repo-browse.service';
import {
  CaseType, CasePriority, OurRole, PartyRole, EventType, PartyType,
  NewCaseRequest, CASE_TYPE_LABELS, EVENT_TYPE_LABELS
} from '../case-management.model';

@Component({
  selector: 'sp-case-new',
  templateUrl: './case-new.component.html',
  styleUrls: ['./case-new.component.scss']
})
export class CaseNewComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  // Wizard state
  currentStep = 1;
  readonly totalSteps = 5; // 4 steps + summary
  submitting = false;
  errorMessage = '';

  // Forms — one per step for independent validation
  step1Form!: FormGroup;     // source reference
  step2Form!: FormGroup;     // case details
  step3Form!: FormGroup;     // parties (array)
  step4Form!: FormGroup;     // first event

  readonly TYPE_LABELS = CASE_TYPE_LABELS;
  readonly EVENT_LABELS = EVENT_TYPE_LABELS;

  readonly caseTypes:    CaseType[]   = ['SUBROGATION','DEFENSE','RECOVERY','FRAUD','EMPLOYMENT','CONTRACT','REGULATORY','OTHER'];
  readonly priorities:   CasePriority[] = ['HIGH','MEDIUM','LOW'];
  readonly roles:        OurRole[]    = ['PLAINTIFF','DEFENDANT'];
  readonly partyRoles:   PartyRole[]   = ['DEFENDANT','PLAINTIFF','WITNESS','COUNSEL_PLAINTIFF','COUNSEL_DEFENDANT','EXPERT'];
  readonly partyTypes:   PartyType[]   = ['INDIVIDUAL','COMPANY','GOVERNMENT'];
  readonly eventTypes:   EventType[]   = ['MENTION','HEARING','FILING_DEADLINE','MEDIATION','SETTLEMENT_MEETING'];

  // Configurable vocabularies — loaded from Reference Data (each sector defines its own).
  sourceTypes: { value: string; label: string }[] = [];
  courtList:   { value: string; label: string }[] = [];

  // Reference number: type manually, look it up from the repository, or let the system assign it.
  refMode: 'manual' | 'lookup' | 'generate' = 'manual';
  refBrowsing = false; refLoading = false;
  refCrumbs: { id: string; name: string }[] = [{ id: '-root-', name: 'Company Home' }];
  refNodes: RepoNode[] = [];

  // Assigned-to user search (reuses the platform user-search endpoint).
  userResults: { id: string; displayName: string; email?: string }[] = [];
  userSearching = false;
  private userTimer: any = null;

  constructor(
    private fb: FormBuilder,
    public router: Router,
    private caseService: CaseManagementService,
    private browse: AlfrescoRepoBrowseService
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.loadConfig();
  }

  private loadConfig(): void {
    this.caseService.referenceItems('case-source-types').subscribe(x => {
      this.sourceTypes = x.length ? x : [{ value: 'STANDALONE', label: 'No source — standalone' }];
      const cur = this.step1Form.value.sourceType;
      if (!this.sourceTypes.some(s => s.value === cur)) {
        this.step1Form.patchValue({ sourceType: this.sourceTypes[0]?.value });
      }
    });
    this.caseService.referenceItems('case-courts').subscribe(x => this.courtList = x);
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  private buildForms(): void {
    this.step1Form = this.fb.group({
      sourceType:        ['INSURANCE_CLAIM', Validators.required],
      sourceRef:         [''],
      sourceDescription: ['']
    });

    this.step2Form = this.fb.group({
      title:        ['', [Validators.required, Validators.minLength(8)]],
      type:         ['SUBROGATION', Validators.required],
      priority:     ['MEDIUM', Validators.required],
      ourRole:      ['PLAINTIFF', Validators.required],
      exposureAmount: [0, [Validators.required, Validators.min(1)]],
      court:        [''],
      courtCaseNumber: [''],
      assignedTo:   ['Susan Wairimu', Validators.required],
      expectedCloseDate: ['']
    });

    this.step3Form = this.fb.group({
      opposingParty:   ['', Validators.required],
      opposingCounsel: [''],
      additionalParties: this.fb.array([])
    });

    this.step4Form = this.fb.group({
      skipFirstEvent: [false],
      title:          ['First mention'],
      type:           ['MENTION'],
      scheduledAt:    [''],
      durationMinutes:[60, [Validators.min(15)]],
      location:       [''],
      reminderDaysBefore: ['7,1']  // Comma list — parsed on submit
    });
  }

  get additionalParties(): FormArray { return this.step3Form.get('additionalParties') as FormArray; }

  addParty(): void {
    this.additionalParties.push(this.fb.group({
      role: ['WITNESS'],
      type: ['INDIVIDUAL'],
      name: ['', Validators.required],
      contact: [''],
      email: ['']
    }));
  }

  removeParty(i: number): void { this.additionalParties.removeAt(i); }

  // ── Reference number: repository lookup / system generate ──────────────────
  setRefMode(m: 'manual' | 'lookup' | 'generate'): void {
    this.refMode = m;
    this.refBrowsing = false;
    if (m === 'generate') { this.step1Form.patchValue({ sourceRef: '' }); }
  }

  openRefBrowser(): void {
    this.refBrowsing = true;
    this.refCrumbs = [{ id: '-root-', name: 'Company Home' }];
    this.loadRef('-root-');
  }
  private loadRef(id: string): void {
    this.refLoading = true;
    this.browse.listChildren(id)
      .then(n => this.refNodes = n)
      .catch(() => this.refNodes = [])
      .finally(() => this.refLoading = false);
  }
  enterRef(n: RepoNode): void { this.refCrumbs.push({ id: n.id, name: n.name }); this.loadRef(n.id); }
  refCrumbTo(i: number): void { this.refCrumbs = this.refCrumbs.slice(0, i + 1); this.loadRef(this.refCrumbs[i].id); }
  /** Pick a document — its name becomes the reference number. */
  pickRef(n: RepoNode): void { this.step1Form.patchValue({ sourceRef: n.name }); this.refBrowsing = false; }
  /** Use the current folder's name as the reference number. */
  useCurrentFolder(): void {
    this.step1Form.patchValue({ sourceRef: this.refCrumbs[this.refCrumbs.length - 1].name });
    this.refBrowsing = false;
  }

  // ── Assigned-to user search ────────────────────────────────────────────────
  onAssigneeInput(term: string): void {
    if (this.userTimer) { clearTimeout(this.userTimer); }
    if (!term || term.trim().length < 2) { this.userResults = []; return; }
    this.userSearching = true;
    this.userTimer = setTimeout(() => {
      this.caseService.searchUsers(term.trim()).pipe(takeUntil(this.destroy$))
        .subscribe(r => { this.userResults = r; this.userSearching = false; });
    }, 300);
  }
  selectAssignee(u: { displayName: string }): void {
    this.step2Form.patchValue({ assignedTo: u.displayName });
    this.userResults = [];
  }

  // ── Inline validation helpers ──────────────────────────────────────────────
  ctrlInvalid(form: FormGroup, name: string): boolean {
    const c = form.get(name);
    return !!c && c.invalid && (c.touched || c.dirty);
  }
  ctrlError(form: FormGroup, name: string): string {
    const c = form.get(name);
    if (!c || !c.errors) { return ''; }
    if (c.errors['required']) { return 'This field is required.'; }
    if (c.errors['minlength']) { return `Minimum ${c.errors['minlength'].requiredLength} characters.`; }
    if (c.errors['min']) { return `Must be at least ${c.errors['min'].min}.`; }
    if (c.errors['email']) { return 'Enter a valid email address.'; }
    return 'Please check this field.';
  }
  private markStep(): void {
    const forms = [null, this.step1Form, this.step2Form, this.step3Form, this.step4Form];
    (forms[this.currentStep] as FormGroup | undefined)?.markAllAsTouched();
  }

  // ── Step navigation ──────────────────────────────────────────────────────
  next(): void {
    if (!this.canAdvance()) { this.markStep(); return; }
    if (this.currentStep < this.totalSteps) this.currentStep++;
  }

  prev(): void {
    if (this.currentStep > 1) this.currentStep--;
  }

  jumpTo(step: number): void {
    if (step <= this.currentStep) this.currentStep = step;
  }

  canAdvance(): boolean {
    switch (this.currentStep) {
      case 1: return this.step1Form.valid;
      case 2: return this.step2Form.valid;
      case 3: return this.step3Form.valid;
      case 4: {
        const skip = this.step4Form.value.skipFirstEvent;
        if (skip) return true;
        const v = this.step4Form.value;
        return !!(v.title && v.scheduledAt && v.location);
      }
      default: return true;
    }
  }

  cancel(): void {
    if (confirm('Discard new case and return to dashboard?')) {
      this.router.navigate(['/case-management']);
    }
  }

  // ── Submit ──────────────────────────────────────────────────────────────
  submit(): void {
    if (!this.canAdvance()) return;
    this.submitting = true;
    this.errorMessage = '';

    const s1 = this.step1Form.value;
    const s2 = this.step2Form.value;
    const s3 = this.step3Form.value;
    const s4 = this.step4Form.value;

    const req: NewCaseRequest = {
      title: s2.title,
      type: s2.type,
      priority: s2.priority,
      ourRole: s2.ourRole,
      sourceRef: s1.sourceRef || undefined,
      sourceType: s1.sourceType !== 'STANDALONE' ? s1.sourceType : undefined,
      sourceDescription: s1.sourceDescription || undefined,
      exposureAmount: Number(s2.exposureAmount),
      opposingParty: s3.opposingParty,
      opposingCounsel: s3.opposingCounsel || undefined,
      assignedTo: s2.assignedTo,
      court: s2.court || undefined,
      courtCaseNumber: s2.courtCaseNumber || undefined,
      expectedCloseDate: s2.expectedCloseDate ? new Date(s2.expectedCloseDate) : undefined,
      parties: (s3.additionalParties || []).map((p: any) => ({
        role: p.role, type: p.type, name: p.name, contact: p.contact, email: p.email
      })),
      firstEvent: !s4.skipFirstEvent && s4.scheduledAt ? {
        title: s4.title,
        type: s4.type,
        scheduledAt: new Date(s4.scheduledAt),
        durationMinutes: Number(s4.durationMinutes),
        location: s4.location,
        attendees: [s2.assignedTo],
        reminderDaysBefore: (s4.reminderDaysBefore || '7,1')
          .toString().split(',').map((x: string) => parseInt(x.trim(), 10)).filter((n: number) => !isNaN(n))
      } : undefined
    };

    this.caseService.createCase(req).pipe(takeUntil(this.destroy$)).subscribe({
      next: created => {
        this.submitting = false;
        this.router.navigate(['/case-management/cases', created.id]);
      },
      error: err => {
        this.submitting = false;
        this.errorMessage = err?.message || 'Failed to create case. Please try again.';
      }
    });
  }

  formatCurrency(v: number): string {
    return new Intl.NumberFormat('en-KE', { style:'currency', currency:'KES', minimumFractionDigits:0 }).format(v);
  }

  stepLabel(step: number): string {
    return ['', 'Source', 'Case Details', 'Parties', 'First Event', 'Review'][step] || '';
  }

  // Summary view helpers
  get summaryData() {
    return {
      s1: this.step1Form.value,
      s2: this.step2Form.value,
      s3: this.step3Form.value,
      s4: this.step4Form.value,
      additionalPartyCount: this.additionalParties.length
    };
  }
}
